'use client';

import posthog from 'posthog-js';
import { PostHogProvider as PostHogContextProvider } from 'posthog-js/react';
import { type ReactNode, useCallback, useEffect, useRef } from 'react';
import { POSTHOG_INIT_OPTIONS, POSTHOG_KEY, isPostHogConfigured } from './config';
import { resolveIdentityTransition } from './identity';
import { flushPendingEvents } from './trackEvent';
import type { PersonProperties } from './types';

type PostHogProviderProps = {
  children: ReactNode;
  /**
   * ログイン中ユーザーの顧客ID。ログアウト中は `null` / `undefined`。
   *
   * 各ログイン経路（メール / 集英社ID / 新規登録）を個別に修正すると漏れるため、
   * セッションを単一の真実として上位（アプリ側 Provider）から渡し、
   * ここで identify / reset の遷移を一元管理する。
   *
   * ⚠️ distinctId は顧客IDのみ。メール / 住所 / 電話などの PII は渡さない（GF-1889）。
   */
  distinctId?: string | null;
  /**
   * identify 時に `$set` するデモグラ属性（gender / ageBand）。
   *
   * identify と同じタイミングで載せることで、匿名 person に属性が付くのを避ける（案B）。
   * PII は含めない（gender / ageBand はデモグラ属性であり PII ではない）。
   */
  personProperties?: PersonProperties;
};

/**
 * PostHog の初期化と Context 提供を行う共通 Provider。
 *
 * - `NEXT_PUBLIC_POSTHOG_KEY` 未設定（テスト / 未構築環境）では init をスキップする（GF-1897）。
 * - ページビューは `capture_pageview: 'history_change'` で自動取得するため hook は不要。
 * - identify / reset は {@link PostHogProviderProps.distinctId} の変化に同期する。
 */
export const PostHogProvider = ({ children, distinctId, personProperties }: PostHogProviderProps) => {
  const previousDistinctIdRef = useRef<string | null>(null);
  const latestIdentityRef = useRef({ distinctId, personProperties });
  latestIdentityRef.current = { distinctId, personProperties };

  const synchronizeIdentityAndFlush = useCallback(() => {
    if (!posthog.__loaded) return;

    const currentIdentity = latestIdentityRef.current;
    // 識別遷移の分岐は純関数に切り出してテスト可能にしている（identity.ts）。
    const action = resolveIdentityTransition(
      previousDistinctIdRef.current,
      currentIdentity.distinctId,
      currentIdentity.personProperties,
    );
    switch (action.type) {
      case 'resetThenIdentify':
        posthog.reset();
        // 匿名状態とログイン後を同一ユーザーとして紐付ける。PII は送らない。
        // personProperties（gender / ageBand）は identify と同時に $set する。
        posthog.identify(action.distinctId, action.personProperties);
        break;
      case 'identify':
        posthog.identify(action.distinctId, action.personProperties);
        break;
      case 'reset':
        posthog.reset();
        break;
      case 'none':
        break;
    }

    previousDistinctIdRef.current = currentIdentity.distinctId ?? null;
    // 初回描画で子の effect が先に発生させたイベントを、ユーザー識別が完了してから送る。
    flushPendingEvents();
  }, []);

  useEffect(() => {
    if (!isPostHogConfigured() || posthog.__loaded) return;
    // POSTHOG_KEY は isPostHogConfigured() で存在を保証済み。
    posthog.init(POSTHOG_KEY as string, {
      ...POSTHOG_INIT_OPTIONS,
      loaded: (client) => {
        POSTHOG_INIT_OPTIONS.loaded?.(client);
        // init が非同期でも、完了通知を起点に最新のユーザーへ紐付けてから送信する。
        synchronizeIdentityAndFlush();
      },
    });
  }, [synchronizeIdentityAndFlush]);

  useEffect(() => {
    synchronizeIdentityAndFlush();
  }, [distinctId, personProperties, synchronizeIdentityAndFlush]);

  return <PostHogContextProvider client={posthog}>{children}</PostHogContextProvider>;
};
